import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { getCustomers, createCustomer, updateCustomer, deleteCustomer, getCustomerHistory, addCustomerNote, getCustomerNote, getBookings, searchUsers } from './businessApi';

const TAG_COLORS = ['#4AC87A', '#5B9CF6', '#C8A84B', '#C05050', '#9B59B6', '#E67E22', '#1ABC9C', '#E91E63'];
const tagColor = (tag) => TAG_COLORS[Math.abs([...tag].reduce((a, c) => a + c.charCodeAt(0), 0)) % TAG_COLORS.length];

const SEGMENTS = [
  { id: 'consumer',  nl: 'Consument',   en: 'Consumer' },
  { id: 'vip',       nl: 'VIP',         en: 'VIP' },
  { id: 'corporate', nl: 'Zakelijk',    en: 'Corporate' },
  { id: 'prospect',  nl: 'Prospect',    en: 'Prospect' },
];
const SOURCES = [
  { id: 'walk-in',  nl: 'Inloop',      en: 'Walk-in' },
  { id: 'referral', nl: 'Doorverwijzing', en: 'Referral' },
  { id: 'website',  nl: 'Website',     en: 'Website' },
  { id: 'social',   nl: 'Sociaal',     en: 'Social' },
  { id: 'other',    nl: 'Anders',      en: 'Other' },
];

const EMPTY_FORM = {
  firstName: '', lastName: '', email: '', phone: '', company: '',
  address: '', birthday: '', tags: [], segment: 'consumer',
  source: 'walk-in', notes: '',
};

const avatarInitial = (c) => ((c.firstName || c.first_name || '?')[0] || '?').toUpperCase();
const fullName = (c) => [c.firstName || c.first_name, c.lastName || c.last_name].filter(Boolean).join(' ') || c.name || 'Klant';

const statusBadge = (status) => {
  const map = {
    confirmed: { color: '#4AC87A', nl: 'Bevestigd', en: 'Confirmed' },
    pending:   { color: '#C8A84B', nl: 'Wachtend',  en: 'Pending' },
    cancelled: { color: '#C05050', nl: 'Geannuleerd', en: 'Cancelled' },
    completed: { color: '#5B9CF6', nl: 'Afgerond',  en: 'Completed' },
    'no-show': { color: '#888',    nl: 'No-show',   en: 'No-show' },
  };
  return map[status] || map['pending'];
};

function Avatar({ customer, size = 36 }) {
  const colors = ['#4AC87A', '#5B9CF6', '#C8A84B', '#9B59B6'];
  const bg = colors[Math.abs((fullName(customer) || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % colors.length];
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `${bg}33`, border: `2px solid ${bg}66`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: bg, fontSize: size * 0.38, fontWeight: 800, flexShrink: 0,
    }}>
      {avatarInitial(customer)}
    </div>
  );
}

function TagPill({ tag, onRemove }) {
  const col = tagColor(tag);
  return (
    <span style={{
      background: `${col}22`, border: `1px solid ${col}55`,
      borderRadius: 12, color: col, fontSize: 11, fontWeight: 600,
      padding: '2px 8px', display: 'inline-flex', alignItems: 'center', gap: 4,
    }}>
      {tag}
      {onRemove && (
        <span onClick={onRemove} style={{ cursor: 'pointer', opacity: 0.7, fontSize: 12 }}>×</span>
      )}
    </span>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div style={{ background: _T.bg3, borderRadius: 14, padding: '14px 16px', border: `1px solid ${_T.cardBorderBot}`, flex: 1, minWidth: 0 }}>
      <div style={{ color: C.silver, fontSize: 11, marginBottom: 4 }}>{label}</div>
      <div style={{ color: C.white, fontSize: 20, fontWeight: 800 }}>{value}</div>
      {sub && <div style={{ color: C.silver2, fontSize: 10, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function BusinessCustomersScreen({ lang, businessId, role, onCreateBooking }) {
  const nl = lang === 'nl';
  const canEdit = role === 'owner' || role === 'admin';

  const [customers, setCustomers] = useState([]);
  const [bookings, setBookings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [filterChip, setFilterChip] = useState('all');
  const [selected, setSelected]   = useState(null); // customer detail
  const [history, setHistory]     = useState([]);
  const [notes, setNotes]         = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [showForm, setShowForm]   = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [tagInput, setTagInput]   = useState('');
  const [saving, setSaving]       = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [showImportInfo, setShowImportInfo] = useState(false);

  // SL user search (for pre-filling new customer form)
  const [slSearch, setSlSearch]   = useState('');
  const [slResults, setSlResults] = useState([]);
  const [slSearching, setSlSearching] = useState(false);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      getCustomers(businessId),
      getBookings(businessId),
    ]).then(([custs, bkgs]) => {
      setCustomers(Array.isArray(custs) ? custs : []);
      setBookings(Array.isArray(bkgs) ? bkgs : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [businessId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Debounced SL user search
  useEffect(() => {
    if (!slSearch.trim() || slSearch.length < 2) { setSlResults([]); return; }
    setSlSearching(true);
    const t = setTimeout(() => {
      searchUsers(slSearch).then(r => {
        setSlResults(Array.isArray(r) ? r.slice(0, 6) : []);
        setSlSearching(false);
      }).catch(() => { setSlResults([]); setSlSearching(false); });
    }, 300);
    return () => clearTimeout(t);
  }, [slSearch]);

  const pickSlUser = (u) => {
    const parts = (u.name || '').trim().split(/\s+/);
    setForm(f => ({
      ...f,
      firstName: parts[0]            || f.firstName,
      lastName:  parts.slice(1).join(' ') || f.lastName,
      email:     u.email             || f.email,
      phone:     u.phone             || f.phone,
    }));
    setSlSearch('');
    setSlResults([]);
  };

  const openDetail = async (c) => {
    setSelected(c);
    setHistLoading(true);
    const [hist, n] = await Promise.all([
      getCustomerHistory(businessId, c.id).catch(() => []),
      getCustomerNote(businessId, c.id).catch(() => []),
    ]);
    setHistory(Array.isArray(hist) ? hist : []);
    setNotes(Array.isArray(n) ? n : []);
    setHistLoading(false);
  };

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditCustomer(null);
    setShowForm(true);
    setSelected(null);
    setSlSearch('');
    setSlResults([]);
  };

  const openEdit = (c) => {
    setForm({
      firstName: c.firstName || c.first_name || '',
      lastName:  c.lastName  || c.last_name  || '',
      email:     c.email     || '',
      phone:     c.phone     || '',
      company:   c.company   || '',
      address:   c.address   || '',
      birthday:  c.birthday  || '',
      tags:      Array.isArray(c.tags) ? c.tags : [],
      segment:   c.segment   || 'consumer',
      source:    c.source    || 'walk-in',
      notes:     '',
    });
    setEditCustomer(c);
    setShowForm(true);
    setSelected(null);
  };

  const handleSave = async () => {
    if (!form.firstName.trim() && !form.lastName.trim() && !form.email.trim()) return;
    setSaving(true);
    const payload = { ...form };
    if (editCustomer) await updateCustomer(businessId, editCustomer.id, payload).catch(() => {});
    else              await createCustomer(businessId, payload).catch(() => {});
    setSaving(false);
    setShowForm(false);
    loadAll();
  };

  const handleDelete = async (cid) => {
    if (!window.confirm(nl ? 'Klant verwijderen?' : 'Delete this customer?')) return;
    await deleteCustomer(businessId, cid).catch(() => {});
    setSelected(null);
    loadAll();
  };

  const handleAddNote = async () => {
    if (!noteInput.trim() || !selected) return;
    setNotesSaving(true);
    const res = await addCustomerNote(businessId, selected.id, { text: noteInput }).catch(() => null);
    if (res) {
      setNotes(prev => [...prev, res]);
      setNoteInput('');
    }
    setNotesSaving(false);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) setForm(f => ({ ...f, tags: [...f.tags, t] }));
    setTagInput('');
  };

  const removeTag = (t) => setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }));

  const exportCSV = () => {
    const rows = [['Name','Email','Phone','Company','Segment','Tags']];
    customers.forEach(c => rows.push([fullName(c), c.email||'', c.phone||'', c.company||'', c.segment||'', (c.tags||[]).join(';')]));
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'customers.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // Filter logic
  const filtered = customers.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || [fullName(c), c.email, c.phone, c.company].some(v => (v||'').toLowerCase().includes(q));
    let matchChip = true;
    if (filterChip === 'vip')      matchChip = c.segment === 'vip' || (c.tags||[]).includes('VIP');
    else if (filterChip === 'active')   matchChip = c.status === 'active' || c.active;
    else if (filterChip === 'inactive') matchChip = c.status === 'inactive' || !c.active;
    else if (filterChip !== 'all')      matchChip = (c.tags||[]).includes(filterChip);
    return matchSearch && matchChip;
  });

  // Stats
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const newThisMonth = customers.filter(c => c.created_at && new Date(c.created_at) >= startOfMonth).length;
  const totalRevenue = bookings.filter(b => b.status === 'completed').reduce((a, b) => a + (parseFloat(b.price) || 0), 0);
  const avgBookings = customers.length > 0 ? (bookings.length / customers.length).toFixed(1) : '0';

  // All tags from customers
  const allTags = [...new Set(customers.flatMap(c => c.tags || []))];

  if (loading) return <div style={{ padding: 48, textAlign: 'center', color: C.silver, fontSize: 13 }}>...</div>;

  // ── Detail view ──
  if (selected && !showForm) {
    const custBookings = bookings.filter(b => b.customer_id === selected.id || b.booker_email === selected.email);
    const totalSpent = custBookings.filter(b => b.status === 'completed').reduce((a, b) => a + (parseFloat(b.price) || 0), 0);
    const lastVisit = custBookings.filter(b => b.status === 'completed').sort((a, b) => new Date(b.date || b.requested_at) - new Date(a.date || a.requested_at))[0];
    return (
      <div style={{ paddingBottom: 32 }}>
        <style>{CSS}</style>
        {/* Back */}
        <button onClick={() => setSelected(null)}
          style={{ background: 'transparent', border: 'none', color: _T.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '0 0 12px', display: 'flex', alignItems: 'center', gap: 6 }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>

        {/* Header card */}
        <div style={{ background: _T.bg3, borderRadius: 18, padding: 20, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 14 }}>
            <Avatar customer={selected} size={52} />
            <div style={{ flex: 1 }}>
              <div style={{ color: C.white, fontSize: 18, fontWeight: 800 }}>{fullName(selected)}</div>
              {selected.company && <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>{selected.company}</div>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
                {(selected.tags || []).map(t => <TagPill key={t} tag={t} />)}
              </div>
            </div>
            {canEdit && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => openEdit(selected)}
                  style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 8, color: C.silver, fontSize: 12, padding: '7px 12px', cursor: 'pointer' }}>
                  {nl ? 'Bewerken' : 'Edit'}
                </button>
                <button onClick={() => handleDelete(selected.id)}
                  style={{ background: '#C0505015', border: '1px solid #C0505044', borderRadius: 8, color: '#C05050', fontSize: 12, padding: '7px 12px', cursor: 'pointer' }}>
                  {nl ? 'Verwijderen' : 'Delete'}
                </button>
              </div>
            )}
          </div>

          {/* Contact */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
            {[
              { icon: '✉️', val: selected.email },
              { icon: '📱', val: selected.phone },
              { icon: '📍', val: selected.address },
              { icon: '🎂', val: selected.birthday },
            ].filter(x => x.val).map((x, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 14 }}>{x.icon}</span>
                <span style={{ color: C.silver, fontSize: 12 }}>{x.val}</span>
              </div>
            ))}
          </div>

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
            {[
              { label: nl ? 'Boekingen' : 'Bookings', val: custBookings.length },
              { label: nl ? 'Uitgegeven' : 'Spent', val: `€${totalSpent.toFixed(0)}` },
              { label: nl ? 'Laatste bezoek' : 'Last visit', val: lastVisit ? new Date(lastVisit.date || lastVisit.requested_at).toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { day: '2-digit', month: 'short' }) : '—' },
              { label: nl ? 'Klant sinds' : 'Since', val: selected.created_at ? new Date(selected.created_at).toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { month: 'short', year: '2-digit' }) : '—' },
            ].map((s, i) => (
              <div key={i} style={{ background: _T.bg4, borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                <div style={{ color: _T.accent, fontSize: 15, fontWeight: 800 }}>{s.val}</div>
                <div style={{ color: C.silver2, fontSize: 10, marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          {onCreateBooking && (
            <button onClick={() => onCreateBooking(selected)}
              style={{ flex: 1, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '12px', cursor: 'pointer' }}>
              + {nl ? 'Nieuwe boeking' : 'New Booking'}
            </button>
          )}
          <button
            style={{ flex: 1, background: `${_T.accent}15`, border: `1px solid ${_T.accent}44`, borderRadius: 12, color: _T.accent, fontSize: 13, fontWeight: 600, padding: '12px', cursor: 'pointer' }}>
            📧 {nl ? 'Stuur boekingsformulier' : 'Send Booking Form'}
          </button>
        </div>

        {/* Booking history */}
        <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 12, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ color: C.white, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{nl ? 'Boekingshistorie' : 'Booking History'}</div>
          {histLoading ? (
            <div style={{ color: C.silver, fontSize: 12, textAlign: 'center', padding: 16 }}>...</div>
          ) : history.length === 0 && custBookings.length === 0 ? (
            <div style={{ color: C.silver2, fontSize: 12, textAlign: 'center', padding: 12 }}>{nl ? 'Geen boekingen' : 'No bookings'}</div>
          ) : (
            (history.length > 0 ? history : custBookings).map((b, i) => {
              const bd = statusBadge(b.status);
              return (
                <div key={b.id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: i < custBookings.length - 1 ? `1px solid ${_T.cardBorderBot}` : 'none' }}>
                  <div>
                    <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{b.service_name || (nl ? 'Service' : 'Service')}</div>
                    <div style={{ color: C.silver2, fontSize: 11, marginTop: 2 }}>
                      {b.date ? new Date(b.date).toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { dateStyle: 'medium' }) : b.requested_at ? new Date(b.requested_at).toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { dateStyle: 'medium' }) : '—'}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: bd.color, background: `${bd.color}18`, border: `1px solid ${bd.color}44`, borderRadius: 8, padding: '3px 8px' }}>
                    {nl ? bd.nl : bd.en}
                  </span>
                </div>
              );
            })
          )}
        </div>

        {/* Notes timeline */}
        <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, border: `1px solid ${_T.cardBorderBot}` }}>
          <div style={{ color: C.white, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>{nl ? 'Coach notities' : 'Coach Notes'}</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              value={noteInput} onChange={e => setNoteInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddNote()}
              placeholder={nl ? 'Notitie toevoegen...' : 'Add a note...'}
              style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '9px 12px', outline: 'none' }}
            />
            <button onClick={handleAddNote} disabled={notesSaving || !noteInput.trim()}
              style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}55`, borderRadius: 10, color: _T.accent, fontSize: 13, fontWeight: 600, padding: '9px 14px', cursor: 'pointer', opacity: noteInput.trim() ? 1 : 0.5 }}>
              {nl ? 'Opslaan' : 'Save'}
            </button>
          </div>
          {notes.length === 0 ? (
            <div style={{ color: C.silver2, fontSize: 12, textAlign: 'center', padding: 12 }}>{nl ? 'Geen notities' : 'No notes yet'}</div>
          ) : (
            [...notes].reverse().map((n, i) => (
              <div key={n.id || i} style={{ paddingBottom: 10, marginBottom: 10, borderBottom: i < notes.length - 1 ? `1px solid ${_T.cardBorderBot}` : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ color: _T.accent, fontSize: 11, fontWeight: 600 }}>{n.author || (nl ? 'Coach' : 'Coach')}</span>
                  <span style={{ color: C.silver2, fontSize: 10 }}>
                    {n.created_at ? new Date(n.created_at).toLocaleString(nl ? 'nl-NL' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                  </span>
                </div>
                <div style={{ color: C.silver, fontSize: 13, lineHeight: 1.5 }}>{n.text}</div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ── Form view ──
  if (showForm) {
    return (
      <div style={{ paddingBottom: 32 }}>
        <style>{CSS}</style>
        <button onClick={() => setShowForm(false)}
          style={{ background: 'transparent', border: 'none', color: _T.accent, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '0 0 14px', display: 'flex', alignItems: 'center', gap: 6 }}>
          ← {nl ? 'Terug' : 'Back'}
        </button>
        <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 16 }}>
          {editCustomer ? (nl ? 'Klant bewerken' : 'Edit Customer') : (nl ? 'Nieuwe klant' : 'New Customer')}
        </div>

        {/* ── SL User Search (new customers only) ── */}
        {!editCustomer && (
          <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 12 }}>
            <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>
              {nl ? '🔍 Zoek bestaande Sense Lifting gebruiker (optioneel)' : '🔍 Search existing Sense Lifting user (optional)'}
            </div>
            <div style={{ position: 'relative' }}>
              <input
                value={slSearch}
                onChange={e => setSlSearch(e.target.value)}
                placeholder={nl ? 'Naam of e-mail...' : 'Name or email...'}
                style={{ width: '100%', boxSizing: 'border-box', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '9px 12px', outline: 'none' }}
              />
              {slSearching && (
                <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: C.silver, fontSize: 12 }}>...</span>
              )}
              {slResults.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: _T.bg2, border: `1px solid ${_T.cardBorderTop}`, borderRadius: 12, marginTop: 4, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                  {slResults.map(u => (
                    <div
                      key={u.id}
                      onClick={() => pickSlUser(u)}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${_T.cardBorderBot}` }}
                      onMouseEnter={e => e.currentTarget.style.background = _T.bg3}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {u.avatar_url
                        ? <img src={u.avatar_url} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                        : <div style={{ width: 32, height: 32, borderRadius: '50%', background: `${_T.accent}33`, border: `1px solid ${_T.accent}55`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: _T.accent, fontWeight: 800, fontSize: 14, flexShrink: 0 }}>{(u.name || '?')[0].toUpperCase()}</div>
                      }
                      <div>
                        <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{u.name}</div>
                        {u.email && <div style={{ color: C.silver, fontSize: 11 }}>{u.email}</div>}
                      </div>
                      <div style={{ marginLeft: 'auto', color: _T.accent, fontSize: 12, fontWeight: 600 }}>
                        {nl ? 'Gebruik' : 'Use'} →
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {slResults.length === 0 && slSearch.length >= 2 && !slSearching && (
              <div style={{ color: C.silver2, fontSize: 11, marginTop: 6 }}>
                {nl ? 'Geen SL gebruikers gevonden — vul handmatig in hieronder.' : 'No SL users found — fill in manually below.'}
              </div>
            )}
          </div>
        )}

        <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, border: `1px solid ${_T.cardBorderBot}`, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            {[
              { key: 'firstName', label: nl ? 'Voornaam *' : 'First name *' },
              { key: 'lastName',  label: nl ? 'Achternaam' : 'Last name' },
            ].map(({ key, label }) => (
              <label key={key} style={{ color: C.silver, fontSize: 11 }}>
                {label}
                <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  style={{ display: 'block', marginTop: 4, width: '100%', boxSizing: 'border-box', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '9px 12px', outline: 'none' }} />
              </label>
            ))}
          </div>
          {[
            { key: 'email',   label: nl ? 'E-mail' : 'Email', type: 'email' },
            { key: 'phone',   label: nl ? 'Telefoon' : 'Phone', type: 'tel' },
            { key: 'company', label: nl ? 'Bedrijf' : 'Company' },
            { key: 'address', label: nl ? 'Adres' : 'Address' },
            { key: 'birthday',label: nl ? 'Geboortedatum' : 'Birthday', type: 'date' },
          ].map(({ key, label, type }) => (
            <label key={key} style={{ color: C.silver, fontSize: 11, display: 'block', marginBottom: 10 }}>
              {label}
              <input type={type || 'text'} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                style={{ display: 'block', marginTop: 4, width: '100%', boxSizing: 'border-box', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '9px 12px', outline: 'none' }} />
            </label>
          ))}

          {/* Tags */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ color: C.silver, fontSize: 11, marginBottom: 6 }}>{nl ? 'Tags' : 'Tags'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
              {form.tags.map(t => <TagPill key={t} tag={t} onRemove={() => removeTag(t)} />)}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder={nl ? 'Tag typen + Enter' : 'Type tag + Enter'}
                style={{ flex: 1, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 12, padding: '7px 10px', outline: 'none' }} />
              <button onClick={addTag} style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}55`, borderRadius: 10, color: _T.accent, fontSize: 12, padding: '7px 12px', cursor: 'pointer' }}>+</button>
            </div>
          </div>

          {/* Segment */}
          <label style={{ color: C.silver, fontSize: 11, display: 'block', marginBottom: 10 }}>
            {nl ? 'Segment' : 'Segment'}
            <select value={form.segment} onChange={e => setForm(f => ({ ...f, segment: e.target.value }))}
              style={{ display: 'block', marginTop: 4, width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '9px 12px', outline: 'none' }}>
              {SEGMENTS.map(s => <option key={s.id} value={s.id}>{nl ? s.nl : s.en}</option>)}
            </select>
          </label>

          {/* Source */}
          <label style={{ color: C.silver, fontSize: 11, display: 'block', marginBottom: 10 }}>
            {nl ? 'Bron' : 'Source'}
            <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
              style={{ display: 'block', marginTop: 4, width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '9px 12px', outline: 'none' }}>
              {SOURCES.map(s => <option key={s.id} value={s.id}>{nl ? s.nl : s.en}</option>)}
            </select>
          </label>

          {/* Notes */}
          {!editCustomer && (
            <label style={{ color: C.silver, fontSize: 11, display: 'block', marginBottom: 10 }}>
              {nl ? 'Eerste notitie (optioneel)' : 'Initial note (optional)'}
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={3} placeholder={nl ? 'Bijv. eerste indruk...' : 'E.g. first impression...'}
                style={{ display: 'block', marginTop: 4, width: '100%', boxSizing: 'border-box', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '9px 12px', outline: 'none', resize: 'vertical' }} />
            </label>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowForm(false)}
            style={{ flex: 1, background: 'transparent', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, color: C.silver, fontSize: 13, padding: '12px', cursor: 'pointer' }}>
            {nl ? 'Annuleren' : 'Cancel'}
          </button>
          <button onClick={handleSave} disabled={saving || (!form.firstName.trim() && !form.email.trim())}
            style={{ flex: 2, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '12px', cursor: 'pointer', opacity: (form.firstName.trim() || form.email.trim()) ? 1 : 0.5 }}>
            {saving ? '...' : (editCustomer ? (nl ? 'Opslaan' : 'Save') : (nl ? 'Klant toevoegen' : 'Add Customer'))}
          </button>
        </div>
      </div>
    );
  }

  // ── List view ──
  return (
    <div style={{ paddingBottom: 32 }}>
      <style>{CSS}</style>

      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto', paddingBottom: 4 }}>
        <StatCard label={nl ? 'Totaal klanten' : 'Total customers'} value={customers.length} />
        <StatCard label={nl ? 'Nieuw deze maand' : 'New this month'} value={newThisMonth} />
        <StatCard label={nl ? 'Gem. boekingen' : 'Avg bookings'} value={avgBookings} />
        <StatCard label={nl ? 'Totaal omzet' : 'Total revenue'} value={`€${totalRevenue.toFixed(0)}`} />
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder={nl ? 'Zoeken op naam, e-mail, telefoon...' : 'Search by name, email, phone...'}
          style={{ flex: 1, background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, color: C.white, fontSize: 13, padding: '10px 14px', outline: 'none' }}
        />
        {canEdit && (
          <button onClick={openNew}
            style={{ background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 12, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '10px 16px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + {nl ? 'Klant' : 'Customer'}
          </button>
        )}
      </div>

      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 14 }}>
        {[
          { id: 'all',      label: nl ? 'Alles' : 'All' },
          { id: 'vip',      label: 'VIP' },
          { id: 'active',   label: nl ? 'Actief' : 'Active' },
          { id: 'inactive', label: nl ? 'Inactief' : 'Inactive' },
          ...allTags.slice(0, 6).map(t => ({ id: t, label: t })),
        ].map(({ id, label }) => (
          <button key={id} onClick={() => setFilterChip(id)}
            style={{ flexShrink: 0, background: filterChip === id ? `${_T.accent}20` : _T.bg3, border: `1.5px solid ${filterChip === id ? _T.accent : _T.cardBorderBot}`, borderRadius: 20, color: filterChip === id ? _T.accent : C.silver, fontSize: 11, fontWeight: filterChip === id ? 700 : 400, padding: '5px 12px', cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      {/* Import / Export row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={() => setShowImportInfo(v => !v)}
          style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 12, padding: '7px 12px', cursor: 'pointer' }}>
          📥 {nl ? 'Importeer CSV' : 'Import CSV'}
        </button>
        <button onClick={exportCSV}
          style={{ background: _T.bg3, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 12, padding: '7px 12px', cursor: 'pointer' }}>
          📤 {nl ? 'Exporteer CSV' : 'Export CSV'}
        </button>
      </div>

      {/* Import instructions */}
      {showImportInfo && (
        <div style={{ background: _T.bg3, borderRadius: 14, padding: 14, marginBottom: 14, border: `1px solid ${_T.accent}44` }}>
          <div style={{ color: C.white, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>{nl ? 'CSV importeren' : 'Import CSV'}</div>
          <div style={{ color: C.silver, fontSize: 12, lineHeight: 1.7 }}>
            {nl
              ? 'Zorg dat je CSV de volgende kolommen bevat: name, email, phone. Optioneel: company, tags (puntkomma gescheiden).'
              : 'Ensure your CSV has these columns: name, email, phone. Optional: company, tags (semicolon separated).'}
          </div>
          <div style={{ fontFamily: 'monospace', background: _T.bg4, borderRadius: 8, padding: '10px 12px', marginTop: 10, color: _T.accent, fontSize: 12 }}>
            name,email,phone,company,tags<br />
            Jan Jansen,jan@example.com,0612345678,Bedrijf BV,VIP;fitness
          </div>
          <button style={{ marginTop: 10, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 10, color: '#0a1220', fontSize: 12, fontWeight: 700, padding: '9px 16px', cursor: 'pointer' }}>
            {nl ? 'Bestand kiezen' : 'Choose file'}
          </button>
        </div>
      )}

      {/* Customer list */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>👥</div>
          <div style={{ fontSize: 15 }}>{nl ? 'Geen klanten gevonden' : 'No customers found'}</div>
          {search && <div style={{ fontSize: 12, color: C.silver2, marginTop: 4 }}>{nl ? 'Probeer een andere zoekterm' : 'Try a different search term'}</div>}
        </div>
      ) : (
        filtered.map(c => {
          const custBookings = bookings.filter(b => b.customer_id === c.id || b.booker_email === c.email);
          const totalSpent = custBookings.filter(b => b.status === 'completed').reduce((a, b) => a + (parseFloat(b.price) || 0), 0);
          const lastVisit = custBookings.filter(b => b.status === 'completed').sort((a, b) => new Date(b.date || b.requested_at) - new Date(a.date || a.requested_at))[0];
          return (
            <div key={c.id} onClick={() => openDetail(c)}
              style={{ background: _T.bg3, borderRadius: 16, padding: 14, marginBottom: 10, border: `1px solid ${_T.cardBorderBot}`, cursor: 'pointer', transition: 'border-color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = `${_T.accent}66`}
              onMouseLeave={e => e.currentTarget.style.borderColor = _T.cardBorderBot}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Avatar customer={c} size={40} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ color: C.white, fontSize: 14, fontWeight: 700 }}>{fullName(c)}</span>
                    {(c.tags || []).slice(0, 3).map(t => <TagPill key={t} tag={t} />)}
                  </div>
                  <div style={{ color: C.silver, fontSize: 12, marginTop: 2 }}>
                    {[c.email, c.phone].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ color: C.white, fontSize: 13, fontWeight: 700 }}>{custBookings.length} bkgs</div>
                  {totalSpent > 0 && <div style={{ color: _T.accent, fontSize: 11, fontWeight: 600 }}>€{totalSpent.toFixed(0)}</div>}
                  {lastVisit && <div style={{ color: C.silver2, fontSize: 10, marginTop: 2 }}>{new Date(lastVisit.date || lastVisit.requested_at).toLocaleDateString(nl ? 'nl-NL' : 'en-GB', { day: '2-digit', month: 'short' })}</div>}
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
